import { useCallback } from 'react';

import { COOKIE_TTL_SECONDS, clearCookie, readCookie, setCookie } from './cookies';

/**
 * Cross-domain navigation logic for the Privisy platform.
 */
export const useAppNavigation = () => {
  const getDashboardUrl = useCallback((path = '') => {
    const baseUrl = process.env.NEXT_PUBLIC_DASHBOARD_URL || process.env.REACT_APP_DASHBOARD_URL || 'http://localhost:4701';
    return `${baseUrl.replace(/\/$/, '')}${path ? `/${path.replace(/^\//, '')}` : ''}`;
  }, []);

  const getMarketingUrl = useCallback((path = '') => {
    const baseUrl = process.env.NEXT_PUBLIC_MARKETING_URL || process.env.REACT_APP_MARKETING_URL || 'http://localhost:4700';
    return `${baseUrl.replace(/\/$/, '')}${path ? `/${path.replace(/^\//, '')}` : ''}`;
  }, []);

  return { getDashboardUrl, getMarketingUrl };
};

/**
 * Anonymous Scan Claiming helpers
 *
 * The cookie primitives themselves live in ./cookies so the tracking-consent
 * accessors can share them without an index -> tracking -> index cycle;
 * `resolveCookieDomain` is re-exported below to keep the package's public API
 * unchanged.
 */
export { resolveCookieDomain } from './cookies';
export {
  UNLOCK_INTENT_COOKIE,
  UNLOCK_INTENT_TTL_SECONDS,
  setUnlockIntent,
  getUnlockIntent,
  clearUnlockIntent,
} from './cookies';

/**
 * Number of CCPA checks in the backend's CHECK_CATALOG (CCPA_CHECK_CATALOG,
 * i.e. every section but the separate CIPA exposure layer, #1392) — the full
 * audit depth quoted in customer-facing copy ("all N checks included").
 * Duplicated here because client bundles must not import packages/backend;
 * parity with CCPA_CHECK_CATALOG.length is enforced by
 * www/src/lib/pricing.test.ts.
 */
export const FULL_AUDIT_CHECK_COUNT = 66;

/**
 * Per-stage breakdown of FULL_AUDIT_CHECK_COUNT, keyed by the same
 * `SectionName` values CHECK_CATALOG entries carry. Duplicated here for the
 * same client-bundle-must-not-import-packages/backend reason as
 * FULL_AUDIT_CHECK_COUNT above, and its own values must sum to it; parity
 * with CHECK_CATALOG is enforced by www/src/lib/pricing.test.ts (issue #1291
 * — these four numbers previously hardcoded a stale 30-check-era split that
 * understated GPC Validation coverage by 6x).
 */
export const CHECK_COUNT_BY_SECTION = {
  uiCompliance: 11,
  trackerDetection: 3,
  gpcValidation: 16,
  privacyPolicy: 36,
} as const;

export const SCAN_INTENT_COOKIE = 'privisy_scan_intent';

export const setScanIntentToken = (token: string) => setCookie(SCAN_INTENT_COOKIE, token, COOKIE_TTL_SECONDS);
export const getScanIntentToken = () => readCookie(SCAN_INTENT_COOKIE);
export const clearScanIntentToken = () => clearCookie(SCAN_INTENT_COOKIE);

/**
 * The anonymous scan id is persisted alongside the intent token (same
 * cross-subdomain cookie semantics) so a pending claim survives the sign-up
 * email-verification redirect, which lands on a clean URL with no
 * ?claimScanId= param. Without this the scanId — read only from the URL —
 * was dropped and the claim never fired.
 */
export const SCAN_INTENT_ID_COOKIE = 'privisy_scan_intent_id';

export const setScanIntentId = (scanId: string) => setCookie(SCAN_INTENT_ID_COOKIE, scanId, COOKIE_TTL_SECONDS);
export const getScanIntentId = () => readCookie(SCAN_INTENT_ID_COOKIE);
export const clearScanIntentId = () => clearCookie(SCAN_INTENT_ID_COOKIE);

/**
 * The tracking-consent cookie (`privisy_consent`) — its name, its read gate
 * (hasConsent), and its accessors — lives in ./tracking, next to the tracker
 * wrappers it gates. The headless consent banner that used to render on top
 * of it (./consent-banner) has no render site in either app since the
 * 2026-07-29 opt-out switch and was deleted (#780) rather than kept as
 * unreachable scaffolding.
 */
export * from './tracking';
export * from './internal-traffic';
export * from './click-id';
export * from './campaign';
export * from './url-validation';
export * from './scan-progress';
export * from './scan-failure';
export * from './scan-client';
export * from './queue-busy';
export * from './start-scan';
export * from './report-email';
